<template>
  <div
    class="p-tree"
    :class="{
      'p-tree-selectable': !!selectionMode,
      'p-tree-loading': loading,
      'p-tree-dragdrop': dragdrop,
      'p-tree-rtl': rtl
    }"
    role="tree"
    :aria-multiselectable="selectionMode === 'multiple' || selectionMode === 'checkbox'"
    :aria-label="ariaLabel"
  >
    <Tree
      :value="value"
      :expandedKeys="expandedKeys"
      :selectionKeys="selectedKeys"
      :selectionMode="selectionMode"
      :loading="loading"
      :dragdrop="dragdrop"
      :filter="filter"
      :filterPlaceholder="filterPlaceholder"
      :filterMode="'lenient'"
      :showIcon="showIcon"
      :togglerIcon="showToggler ? undefined : 'none'"
      :loadingIcon="showLoader ? undefined : 'none'"
      :lines="showLines"
      :scrollHeight="scrollHeight"
      @node-expand="(node) => $emit('node-expand', node as unknown as TreeNode)"
      @node-collapse="(node) => $emit('node-collapse', node as unknown as TreeNode)"
      @node-select="(node) => $emit('node-select', node as unknown as TreeNode)"
      @node-unselect="(node) => $emit('node-unselect', node as unknown as TreeNode)"
      @update:expandedKeys="$emit('update:expandedKeys', $event)"
      @update:selectionKeys="$emit('update:selectedKeys', $event)"
      @filter="$emit('filter', $event)"
      :pt="{
        root: { class: 'p-tree-container' },
        node: { class: 'p-treenode' },
        content: { class: 'p-treenode-content' },
        toggler: { class: 'p-tree-toggler' }
      }"
    >
      <template #loading v-if="$slots.loading">
        <slot name="loading"></slot>
      </template>
      <template #empty v-if="$slots.empty">
        <slot name="empty"></slot>
      </template>
      <template #toggler="slotProps" v-if="$slots.toggler">
        <slot name="toggler" :node="slotProps.node as unknown as TreeNode"></slot>
      </template>
    </Tree>
    <div v-if="loading" class="p-tree-loading-overlay">
      <i class="pi pi-spinner pi-spin"></i>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Tree from 'primevue/tree'
import type { TreeNode, TreeProps, TreeEmits } from './types'

export default defineComponent({
  name: 'MOPTree',
  components: {
    Tree
  },
  props: {
    value: {
      type: Array as () => TreeNode[],
      required: true
    },
    expandedKeys: {
      type: Object as () => Record<string, boolean>,
      default: () => ({})
    },
    selectedKeys: {
      type: Object as () => Record<string, boolean>,
      default: () => ({})
    },
    selectionMode: {
      type: String as () => 'single' | 'multiple' | 'checkbox' | undefined,
      default: 'single'
    },
    loading: {
      type: Boolean,
      default: false
    },
    dragdrop: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    showToggler: {
      type: Boolean,
      default: true
    },
    showLoader: {
      type: Boolean,
      default: true
    },
    showLines: {
      type: Boolean,
      default: false
    },
    keyboardNavigation: {
      type: Boolean,
      default: true
    },
    filter: {
      type: Boolean,
      default: false
    },
    filterPlaceholder: {
      type: String,
      default: 'Search'
    },
    scrollHeight: {
      type: String,
      default: null
    },
    rtl: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: 'Tree'
    }
  },
  emits: [
    'node-expand',
    'node-collapse',
    'node-select',
    'node-unselect',
    'update:expandedKeys',
    'update:selectedKeys',
    'filter'
  ]
})
</script>
